import {apiRequest} from './api';
import {OrganizerDto} from '../../generated/models/OrganizerDto';
import {
  pendingBusinessesEndpoint,
  pendingClaimsEndpoint,
  updateAccountStatusEndpoint,
  updateClaimStatusEndpoint,
} from '../constants/endpoints';

// Per-place claim approval status.
export enum ClaimPlaceStatus {
  PendingApproval = 1,
  Approved = 2,
  NotApproved = 3,
  DataVerifying = 4,
  NotClaimed = 5,
}

// Business account approval status (organizer level).
export enum OrganizerAccountStatus {
  PendingApproval = 1,
  Approved = 2,
  NotApproved = 3,
}

// The list endpoints have returned the organizer array bare, wrapped as
// {data: [...]}, or wrapped as {data: {value: [...]}} (the Result envelope the
// organizer-search endpoint also uses). Pull the array out of whichever shape
// came back so an empty or unexpected payload can never crash the caller.
export const extractOrganizers = (response: unknown): OrganizerDto[] => {
  const candidates: unknown[] = [
    response,
    (response as {data?: unknown} | null)?.data,
    (response as {data?: {value?: unknown}} | null)?.data?.value,
  ];
  const array = candidates.find(candidate => Array.isArray(candidate));
  return (array as OrganizerDto[] | undefined) ?? [];
};

// Throws when the request fails, so the screen can tell a failure from an
// empty list.
export const fetchPendingClaims = async (): Promise<OrganizerDto[]> => {
  let failed = false;
  const response = await apiRequest<OrganizerDto[]>(
    {endpoint: pendingClaimsEndpoint, method: 'GET'},
    {
      errorMessage: 'Could not load the pending claims',
      onError: () => {
        failed = true;
      },
    },
  );

  if (failed) {
    throw new Error('Could not load the pending claims');
  }

  return extractOrganizers(response);
};

// Throws when the request fails, so the screen can tell a failure from an
// empty list.
export const fetchPendingBusinesses = async (): Promise<OrganizerDto[]> => {
  let failed = false;
  const response = await apiRequest<OrganizerDto[]>(
    {endpoint: pendingBusinessesEndpoint, method: 'GET'},
    {
      errorMessage: 'Could not load the pending businesses',
      onError: () => {
        failed = true;
      },
    },
  );

  if (failed) {
    throw new Error('Could not load the pending businesses');
  }

  return extractOrganizers(response);
};

export const updatePlaceClaimStatus = async (
  placeId: string,
  status: ClaimPlaceStatus,
): Promise<boolean> => {
  const response = await apiRequest({
    endpoint: updateClaimStatusEndpoint(placeId, status),
    method: 'PATCH',
  });

  return response !== null;
};

export const updateOrganizerAccountStatus = async (
  organizerId: string,
  status: OrganizerAccountStatus,
): Promise<boolean> => {
  const response = await apiRequest({
    endpoint: updateAccountStatusEndpoint(organizerId, status),
    method: 'PATCH',
  });

  return response !== null;
};
